import { RpiTable } from "../../rpi-core/table/RpiTable";
import { GraphQL, GraphQLPagedQueryData } from "@rpi/openapi-api";
import { useAuthUser } from "../../hooks/useAuthUser";
import { useNavigateRoute } from "../../hooks/useNavigateRoute";
import { tokens } from "@blurb/codex-react";
import { createStyles, Menu } from "@mantine/core";
import { IconV3 } from "@rpi/openapi-core";
import {
  customerProductColumnsSuperAdmin,
  customerProductColumnsV2,
} from "../../configuration/columns/customerProductV2.column";
import { RpaTableMenu } from "../../components/RpaTableMenu";
import React from "react";

type CustomerProductsType = GraphQLPagedQueryData<GraphQL.CustomerProductsQuery>;
type CustomerProductsSuperAdminType = GraphQLPagedQueryData<GraphQL.CustomerProductsSuperAdminQuery>;

const useTableStyle = createStyles({
  wrapper: {
    border: `1px solid ${tokens.color.semantic.border.faint}`,
    borderRadius: tokens.radius.lg,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",

    "& .footer": {
      display: "flex",
      borderTop: `1px solid ${tokens.color.semantic.border.faint}`,
    },
  },
});

export interface CustomerProductsTableV2Props {
  customerProducts?: (CustomerProductsType | CustomerProductsSuperAdminType)[];
  isLoading: boolean;
  error?: unknown;
  setIsPriceCalculatorOpen: (open: boolean) => void;
  setProductId: (id: number | undefined) => void;
}

export function CustomerProductsTableV2({
  customerProducts,
  isLoading,
  error,
  setIsPriceCalculatorOpen,
  setProductId,
  children,
}: React.PropsWithChildren<CustomerProductsTableV2Props>) {
  const { classes } = useTableStyle();
  const user = useAuthUser();
  const columnDefs = user.is.SUPER_ADMIN ? customerProductColumnsSuperAdmin : customerProductColumnsV2;

  return (
    <div className={classes.wrapper}>
      <RpiTable
        variant="normalV2"
        isLoading={isLoading}
        error={error}
        emptyMessage={"No products found."}
        perPage={customerProducts?.length}
        data={customerProducts}
        columns={columnDefs}
        additionalColumns={[
          {
            header: "",
            maxSize: 36,
            minSize: 36,
            size: 36,
            align: "center",
            noPadding: true,
            cell(data) {
              return (
                <ProductsActionMenu
                  customerProductId={data.row.id}
                  onOpenPriceCalculator={() => {
                    setProductId(data.row.id);
                    setIsPriceCalculatorOpen(true);
                  }}
                />
              );
            },
          },
        ]}
      />
      {children}
    </div>
  );
}

export function CustomerProductsTableFooter({ children }: React.PropsWithChildren) {
  return <div className="footer">{children}</div>;
}

type ProductsActionMenuProps = {
  customerProductId: number;
  onOpenPriceCalculator: () => void;
};
function ProductsActionMenu({ customerProductId, onOpenPriceCalculator }: ProductsActionMenuProps) {
  const navigateRoute = useNavigateRoute();
  return (
    <RpaTableMenu>
      <Menu.Item
        icon={<IconV3 icon="description" size={20} />}
        onClick={() => navigateRoute(["customer-product", { id: customerProductId }])}
      >
        View Details
      </Menu.Item>
      <Menu.Item icon={<IconV3 icon="attach_money" size={20} />} onClick={onOpenPriceCalculator}>
        Price Calculator
      </Menu.Item>
    </RpaTableMenu>
  );
}
